import type { StatusTone } from "@/shared/ui";

/**
 * Badge tone for a subscription status.
 *
 * Lives in the entity because TWO widgets need it — the subscriptions list and
 * the store detail page's Subscription card — and `widgets/system-admin/stores`
 * may not import from `widgets/system-admin/subscriptions`: same layer, different
 * slice. Duplicating the map is how the two would drift into disagreeing about
 * what TRIALING looks like.
 *
 * The backend's `SubscriptionStatus` is
 * `TRIALING | ACTIVE | PAST_DUE | UNPAID | EXPIRED | CANCELED`. This map once
 * matched only two of those six: it spelled `CANCELED` with two Ls, used `TRIAL`
 * for `TRIALING`, and had no `PAST_DUE` or `UNPAID` — so a cancelled or past-due
 * subscription looked no different from a healthy one, on the page whose whole
 * job is telling them apart.
 *
 * Both legacy spellings are kept rather than replaced: the enum is relayed from
 * the backend team and is not in the OpenAPI export, so we cannot confirm no path
 * still emits the old ones, and keeping both cannot render worse than the miss it
 * replaces.
 */
export const SUBSCRIPTION_STATUS_TONES: Record<string, StatusTone> = {
  TRIALING: "info",
  ACTIVE: "success",
  PAST_DUE: "warning",
  UNPAID: "danger",
  EXPIRED: "warning",
  CANCELED: "danger",
  // Legacy spellings, retained deliberately — see above.
  CANCELLED: "danger",
  TRIAL: "info",
};

/** Neutral for anything unrecognised: a status we cannot read must not be
 *  reported as healthy. */
export const subscriptionStatusTone = (status?: string): StatusTone =>
  (status && SUBSCRIPTION_STATUS_TONES[status]) || "neutral";
